import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TicketAuditEntry, TicketMessagePayload } from '@nbh/shared';
import { api } from '../../lib/api';
import { cacheFirst } from '../../lib/cacheFirst';
import { cacheTicketThread, getCachedTicketThread } from '../../lib/db';
import { useAuth } from '../../auth/AuthProvider';
import { useRealtime } from '../../hooks/useRealtime';
import { ChatComposer } from '../chat/ChatComposer';
import { ChatMessageList } from './ChatMessageList';

export interface TicketChatProps {
  ticketId: string;
  /** Vendors cannot post internal notes, so the toggle is hidden for them. */
  canPostInternal: boolean;
  /** The job's trail, for the lifecycle rows between the messages (D5). */
  events?: TicketAuditEntry[];
}

/**
 * Live work-order chat (SPEC.md §5.3).
 *
 * Renders two siblings rather than one box, because the detail page places them apart:
 * the thread fills the tab, and the composer takes the action toolbar's pinned slot above
 * the tab bar (D53). Three things used to compete for the bottom of a 380px screen — the
 * tab bar, the fixed toolbar and this composer — and the composer lost: it sat in the
 * page flow, below the fold, hidden behind Hold and Resolve.
 *
 * Reads the cache before the network (Rule 4): a technician re-reading the thread in a
 * plant room is the normal case. Sending still needs the network and says so.
 */
export function TicketChat({ ticketId, canPostInternal, events = [] }: TicketChatProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState<TicketMessagePayload[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setError(null);
    cancelRef.current?.();
    cancelRef.current = cacheFirst<TicketMessagePayload[]>({
      readCache: () => getCachedTicketThread(ticketId),
      load: () => api.listMessages(ticketId),
      writeCache: (rows) => cacheTicketThread(ticketId, rows),
      onValue: setMessages,
      onError: (caught) => {
        setMessages((current) => current ?? []);
        setError(caught.message);
      },
    });
    return () => cancelRef.current?.();
  }, [ticketId]);

  const receive = useCallback(
    (payload: TicketMessagePayload) => {
      if (payload.ticketId !== ticketId) return;
      // Guards the race between our own POST response and the broadcast.
      setMessages((current) => {
        const known = current ?? [];
        if (known.some((message) => message.id === payload.id)) return current;
        const next = [...known, payload];
        // The cache follows the screen, so a thread read live is the thread read offline.
        void cacheTicketThread(ticketId, next);
        return next;
      });
    },
    [ticketId],
  );

  useRealtime({ onMessage: receive }, ticketId);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages?.length]);

  async function send(event: FormEvent) {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;

    setSending(true);
    setError(null);
    try {
      receive(await api.postMessage(ticketId, { body, isInternal }));
      setDraft('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('messages.sendFailed'));
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div className="flex-1 pb-2">
        {error && messages?.length === 0 && (
          <p role="alert" className="px-4 pt-3 text-sm text-red-400">
            {error}
          </p>
        )}
        <ChatMessageList
          messages={messages ?? []}
          events={events}
          viewerId={user?.id}
          loading={messages === null}
        />
        {/*
          The scroll target. The composer below is sticky and overlays whatever sits
          behind it, and the tab bar under that is fixed, so scrolling this sentinel to
          the viewport's edge would park the newest bubble under the input. The margin
          is both of them: the bar's own height plus the composer with its toggle.
        */}
        <div ref={endRef} className="[scroll-margin-bottom:calc(var(--nav-h)+8.5rem)]" />
      </div>

      {/*
        The toolbar's slot: pinned above the tab bar by the bar's own height, which already
        includes the home-indicator inset. The page hides the action toolbar on this tab so
        the two never stack (D53).
      */}
      <ChatComposer
        slot="composer"
        draft={draft}
        onDraft={setDraft}
        onSubmit={send}
        busy={sending}
        maxLength={2000}
        placeholder={t('chat.placeholder')}
        sendLabel={t('chat.send')}
        className="sticky bottom-[var(--nav-h)] bg-slate-950/95 backdrop-blur"
      >
        {error && messages !== null && messages.length > 0 && (
          <p role="alert" className="text-sm text-red-400">
            {error}
          </p>
        )}
        {canPostInternal && (
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(event) => setIsInternal(event.target.checked)}
              className="size-4 min-h-0"
            />
            {t('chat.markInternal')}
          </label>
        )}
      </ChatComposer>
    </>
  );
}
